import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "register-page" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = { class: "page-content" }
const _hoisted_4 = { class: "tab-header" }
const _hoisted_5 = { class: "title" }
const _hoisted_6 = { class: "actions" }
const _hoisted_7 = { class: "tab-content" }

import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { platformGet } from "@/common/api/rest/platform";
import { groupList, groupAdd, groupDel } from "@/common/api/rest/group";

import type { PlatformType } from "@/common/types/platform";
import type { GroupType, GroupPayloadType } from "@/common/types/group";

import { FormGroup } from "@/components";
import {
  breadcrumbSimple,
  baseConfirm,
  baseModal,
  baseTable,
  iconMenu,
  svgIcon,
} from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const platformData = ref<PlatformType | null>(null);

const tableData = ref<GroupType[]>([]);
const payload = ref<GroupPayloadType | null>(null);

const selected = ref<GroupType | null>(null);

const limit = ref<number>(10);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);

const platformId = computed(() => route.params.platform as string);

const tableHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "status", label: t("common.labels.status") },
  { key: "slug", label: t("common.labels.slug") },
]);

const breadcrumbList = computed(() => [
  {
    key: "register",
    label: t("common.labels.registrations"),
    url: "/register",
  },
  {
    key: "platforms",
    label: t("common.labels.platforms"),
    url: `/register/platform`,
  },
  {
    key: "platform",
    label: platformData.value?.name ?? "...",
    url: `/register/platform/${platformId.value}/edit`,
  },
  {
    key: "groups",
    label: t("group.labels.groups"),
    url: `/register/platform/${platformId.value}/group`,
  },
]);

const menuList = computed(() => [
  {
    key: "platform",
    icon: "home-line",
    label: t("platform.labels.platform"),
    url: `/register/platform/${platformId.value}/edit`,
  },
  {
    key: "group",
    icon: "field",
    label: t("group.labels.groups"),
    url: `/register/platform/${platformId.value}/group`,
  },
]);

async function fetchPlatform() {
  await platformGet(platformId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        platformData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchData() {
  await groupList(platformId.value, {
    page: page.value,
    limit: limit.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function searchItem(value: string) {
  search.value = value;

  fetchData();
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addItemConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  groupAdd(platformId.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        editItem(data);
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editItem(data: GroupType) {
  router.push(
    `/register/platform/${data.platform_id}/group/${data.group_id}/edit`
  );
}

function removeItem(data: GroupType) {
  selected.value = data;
  showModalDel.value = true;
}

function removeItemCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function removeItemConfirm() {
  if (!selected.value) return;

  groupDel(selected.value.group_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeItemCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeMenu(url: string) {
  router.push(url);
}

function back() {
  router.push("/register/platform");
}

onMounted(async () => {
  await fetchPlatform();
  await fetchData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("article", _hoisted_1, [
      _createElementVNode("section", _hoisted_2, [
        _createVNode(_unref(breadcrumbSimple), {
          items: breadcrumbList.value,
          "show-home": true,
          "url-home": "/home",
          active: "groups"
        }, null, 8, ["items"])
      ]),
      _createElementVNode("section", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(svgIcon), {
              icon: "platform",
              size: "2rem"
            }),
            _createTextVNode(" " + _toDisplayString(_unref(t)("platform.labels.edit_platform")), 1)
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("button", {
              class: "btn btn-default",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (back()))
            }, [
              _createVNode(_unref(svgIcon), {
                icon: "close",
                size: "1rem"
              })
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(_unref(iconMenu), {
            tabs: menuList.value,
            active: "group",
            size: "3rem",
            onSelected: _cache[1] || (_cache[1] = ($event: any) => (changeMenu($event)))
          }, null, 8, ["tabs"]),
          _createVNode(_unref(baseTable), {
            header: tableHeader.value,
            data: tableData.value,
            limit: limit.value,
            page: page.value,
            pages: pages.value,
            total: total.value,
            searched: searched.value,
            options: true,
            del: true,
            edit: true,
            "onChange:page": _cache[2] || (_cache[2] = ($event: any) => (changePage($event))),
            onSearching: _cache[3] || (_cache[3] = ($event: any) => (searchItem($event))),
            "onBtn:add": _cache[4] || (_cache[4] = ($event: any) => (addItem())),
            "onBtn:edit": _cache[5] || (_cache[5] = ($event: any) => (editItem($event))),
            "onBtn:del": _cache[6] || (_cache[6] = ($event: any) => (removeItem($event)))
          }, {
            status: _withCtx(({ data }) => [
              (data.status === 'ACTIVE')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode(_toDisplayString(_unref(t)("common.labels.active")), 1)
                  ], 64))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode(_toDisplayString(_unref(t)("common.labels.inactive")), 1)
                  ], 64))
            ]),
            _: 1
          }, 8, ["header", "data", "limit", "page", "pages", "total", "searched"])
        ])
      ])
    ]),
    _createVNode(_unref(baseModal), {
      show: showModalAdd.value,
      title: _unref(t)('group.modals.titles.add_group').toUpperCase(),
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.save'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[9] || (_cache[9] = ($event: any) => (addItemCancel())),
      onConfirm: _cache[10] || (_cache[10] = ($event: any) => (addItemConfirm()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(FormGroup), {
          "onUpdate:data": _cache[7] || (_cache[7] = ($event: any) => (payload.value = $event)),
          onValidate: _cache[8] || (_cache[8] = ($event: any) => (payloadValid.value = $event))
        })
      ]),
      _: 1
    }, 8, ["show", "title", "button-confirm", "button-close"]),
    _createVNode(_unref(baseConfirm), {
      show: showModalDel.value,
      title: _unref(t)('group.modals.titles.del_group').toUpperCase(),
      message: 
      _unref(t)('group.modals.messages.del_group', {
        name: selected.value?.name?.toUpperCase(),
      })
    ,
      onCancel: _cache[11] || (_cache[11] = ($event: any) => (removeItemCancel())),
      onConfirm: _cache[12] || (_cache[12] = ($event: any) => (removeItemConfirm()))
    }, null, 8, ["show", "title", "message"])
  ], 64))
}
}

})